import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LeadFileService } from '../../../pages/leads/services/lead-file.service';
import { LeadFile } from '../../../pages/leads/models/lead-file';
import { FileUploadService } from '../../../pages/documents/services/file-upload.service';
import { FileDetails } from '../../../pages/documents/models/file-details';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { Connection } from '../../../pages/outreach/models/connection.model';
import {
  BusinessFiles,
  OutreachAttachment,
} from '../../models/company-details';
import { PopupComponent } from '../popup/popup.component';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { takeUntil, forkJoin } from 'rxjs';
import { FileViewerComponent } from '../file-viewer/file-viewer.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { PermissionCode } from '../../enums/permission-code';
import { ToastType } from '../../interfaces/common';
import { CommonService } from '../../services/common/common.service';

@Component({
  selector: 'app-record-attachments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PopupComponent,
    FileViewerComponent,
    PaginationComponent,
    DatePipe,
  ],
  templateUrl: './record-attachments.component.html',
  styleUrl: './record-attachments.component.scss',
})
export class RecordAttachmentsComponent
  extends DestroyBase
  implements OnInit, OnChanges
{
  private commonService = inject(CommonService);

  @Input() recordUniqueId: string | null = null;
  @Input() assignedConnections: Connection[] = [];
  @Input() outreachAttachments: OutreachAttachment[] = [];
  @Input() businessFiles: BusinessFiles[] = [];

  @Input() set refresh(val: boolean) {
    if (val) this.loadFiles();
  }

  files: LeadFile[] = [];
  isLoading = false;

  currentPage = 1;
  pageSize = 10;
  totalItems = 0;

  // Modal states
  showAddFileOptionModal = false;
  showAttachModal = false;
  showShareModal = false;

  // Attach Flow state
  isUploading = false;
  isUploadInProgress = false;
  isAttaching = false;
  selectedFileIds = new Set<number>();
  uploadedFileId: number | null = null;
  selectedUploadFile: File | null = null;
  hasConsentChecked = false;
  validationErrors: string[] = [];
  isDragging = false;

  readonly allowedExtensions = [
    'doc',
    'xlsx',
    'xls',
    'odt',
    'pdf',
    'txt',
    'docx',
    'zip',
    'csv',
    'xlsm',
  ];

  // Library Picker state
  isLoadingLibraryFiles = false;
  libraryFiles: FileDetails[] = [];
  librarySearchQuery = '';
  totalUploadedFilesCount = 0;

  get filteredLibraryFiles(): FileDetails[] {
    if (!this.librarySearchQuery) return this.libraryFiles;
    const q = this.librarySearchQuery.toLowerCase();
    return this.libraryFiles.filter(
      (f) =>
        f.blob_name?.toLowerCase().includes(q) ||
        f.file_name?.toLowerCase().includes(q),
    );
  }

  // Visibility/Sharing state
  visibilityMode: 'me' | 'org' | 'selected' = 'me';
  selectedConnectionIds = new Set<number>();
  sortedConnections: Connection[] = [];

  // Manage Share state
  editingFileId: number | null = null;
  isLoadingSharing = false;
  isSavingSharing = false;
  initialSharedIds = new Set<number>();
  isAllSelectedVal = false;

  // Delete state
  showDeleteModal = false;
  fileToDelete: LeadFile | null = null;
  isDeleting = false;

  // File Viewer properties
  showFileViewer = false;
  viewerFileUrl = '';
  viewerFileName = '';
  viewerFileId: number | null = null;

  private leadFileService = inject(LeadFileService);
  private fileUploadService = inject(FileUploadService);
  private authService = inject(AuthService);

  hasUploadRecordsPermission = this.authService.hasPermission(
    PermissionCode.UPLOAD_RECORDS,
  );

  // Not used directly in template but useful for logic
  currentUserConnectionId: number | null = null;

  displayToast(message: string, type: 'success' | 'error' = 'success') {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message: message,
    });
  }

  ngOnInit(): void {
    if (this.recordUniqueId) {
      this.loadFiles();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assignedConnections'] || changes['recordUniqueId']) {
      // Re-evaluate current user connection when assignedConnections changes
      if (this.assignedConnections && this.assignedConnections.length > 0) {
        const currentUser = this.authService.getLoggedInUserInfo();
        if (currentUser && currentUser.connection) {
          const foundConn = this.assignedConnections.find(
            (c) => c.connection_name === currentUser.connection,
          );
          this.currentUserConnectionId = foundConn ? foundConn.id : null;
        }
        // Sort connections: Current user's connection first, then others
        this.sortedConnections = [...this.assignedConnections].sort((a, b) => {
          if (a.id === this.currentUserConnectionId) return -1;
          if (b.id === this.currentUserConnectionId) return 1;
          return (a.display_name || a.connection_name).localeCompare(
            b.display_name || b.connection_name,
          );
        });
      } else {
        this.sortedConnections = [];
      }
    }

    if (changes['recordUniqueId'] && !changes['recordUniqueId'].firstChange) {
      this.loadFiles();
    }
  }

  loadFiles() {
    if (!this.recordUniqueId) {
      this.files = [];
      this.totalItems = 0;
      return;
    }
    this.isLoading = true;
    this.leadFileService
      .getLeadFiles(this.recordUniqueId, this.currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.files = res.results;
          this.totalItems = res.count;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load lead files', err);
          this.isLoading = false;
        },
      });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadFiles();
  }

  openAddFileModal() {
    this.openLibraryModal();
  }

  openUploadModal() {
    this.showAddFileOptionModal = false;
    this.isUploading = true;
    this.resetAttachState();
    this.showAttachModal = true;
  }

  openLibraryModal() {
    this.showAddFileOptionModal = false;
    this.isUploading = false;
    this.resetAttachState();
    this.showAttachModal = true;
    this.loadLibraryFiles();
  }

  resetAttachState() {
    this.selectedFileIds = new Set<number>();
    this.uploadedFileId = null;
    this.selectedUploadFile = null;
    this.hasConsentChecked = false;
    this.validationErrors = [];
    this.isUploadInProgress = false;
    this.isAttaching = false;
    this.visibilityMode = 'me';
    this.selectedConnectionIds.clear();
    this.isDragging = false;
    this.totalUploadedFilesCount = 0;
    this.librarySearchQuery = '';
  }

  loadLibraryFiles() {
    if (!this.recordUniqueId) {
      this.libraryFiles = [];
      return;
    }
    this.isLoadingLibraryFiles = true;
    const currentUser = this.authService.getLoggedInUserInfo()?.username || '';

    forkJoin({
      linkedRes: this.leadFileService.getLeadFiles(
        this.recordUniqueId,
        1,
        1000,
      ),
      libRes: this.fileUploadService.getAllUploadedFiles(
        1,
        1000,
        false,
        undefined,
        undefined,
        undefined,
        currentUser,
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ linkedRes, libRes }) => {
          const linkedFileIds = new Set(
            linkedRes.results.map((f) => f.file?.id).filter(Boolean),
          );
          this.totalUploadedFilesCount = libRes.results.length;
          this.libraryFiles = libRes.results.filter(
            (f) => !linkedFileIds.has(f.id) && f.upload_status === 2,
          );
          this.isLoadingLibraryFiles = false;
        },
        error: (err) => {
          console.error('Failed to load library files', err);
          this.isLoadingLibraryFiles = false;
        },
      });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.selectedUploadFile = file;
      this.hasConsentChecked = false;
      this.validationErrors = [];

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !this.allowedExtensions.includes(fileExtension)) {
        this.validationErrors.push(
          `Invalid file type. Supported extensions are: ${this.allowedExtensions.join(', ')}.`,
        );
      }
      if (file.size > 1025000000) {
        this.validationErrors.push('File size should be less than 1 GB.');
      }
    }
    input.value = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.selectedUploadFile = file;
      this.hasConsentChecked = false;
      this.validationErrors = [];

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !this.allowedExtensions.includes(fileExtension)) {
        this.validationErrors.push(
          `Invalid file type. Supported extensions are: ${this.allowedExtensions.join(', ')}.`,
        );
      }
      if (file.size > 1025000000) {
        this.validationErrors.push('File size should be less than 1 GB.');
      }
    }
  }

  removeSelectedFile() {
    this.selectedUploadFile = null;
    this.hasConsentChecked = false;
    this.validationErrors = [];
    const fileInput = document.getElementById(
      'fileUploadInput',
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  toggleFileSelection(fileId: number) {
    if (this.selectedFileIds.has(fileId)) {
      this.selectedFileIds.delete(fileId);
    } else {
      this.selectedFileIds.add(fileId);
    }
  }

  toggleConnection(connId: number) {
    if (this.selectedConnectionIds.has(connId)) {
      this.selectedConnectionIds.delete(connId);
    } else {
      this.selectedConnectionIds.add(connId);
    }
  }

  attachSelectedFile() {
    if (!this.recordUniqueId) return;

    if (this.isUploading) {
      if (!this.selectedUploadFile || !this.hasConsentChecked) return;
      this.isAttaching = true;
      this.fileUploadService
        .uploadFile(this.selectedUploadFile, [])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.uploadedFileId = res.file_id;
            this.selectedFileIds = new Set([res.file_id]);
            this.proceedWithAttach();
          },
          error: (err) => {
            console.error('Upload failed', err);
            this.isAttaching = false;
            this.validationErrors = [];

            const nonFieldError = err.error?.non_field_errors?.[0];
            if (nonFieldError && nonFieldError.includes('Missing columns:')) {
              const columnsString = nonFieldError
                .split('Missing columns:')[1]
                .trim();
              this.validationErrors.push('Missing columns:');
              const missingColumns = columnsString
                .split(',')
                .map((col: string) => col.trim());
              missingColumns.forEach((column: string) => {
                this.validationErrors.push(column);
              });
            } else if (nonFieldError) {
              this.validationErrors.push(nonFieldError);
            } else if (err.error?.file_name) {
              this.validationErrors.push(err.error.file_name[0]);
            } else {
              this.displayToast('File upload failed', 'error');
            }
          },
        });
    } else {
      if (this.selectedFileIds.size === 0) return;
      this.isAttaching = true;
      this.proceedWithAttach();
    }
  }

  proceedWithAttach() {
    if (!this.recordUniqueId) return;

    let connectionIds: number[] = [];

    if (this.visibilityMode === 'org') {
      if (this.currentUserConnectionId) {
        connectionIds = [this.currentUserConnectionId];
      }
    } else if (this.visibilityMode === 'selected') {
      connectionIds = Array.from(this.selectedConnectionIds);
    }

    this.leadFileService
      .linkFileToLead(this.recordUniqueId, {
        file_ids: Array.from(this.selectedFileIds),
        connection_ids: connectionIds.length > 0 ? connectionIds : undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isAttaching = false;
          this.closeAttachModal();
          this.currentPage = 1; // Reset to first page to see new file
          this.loadFiles(); // Refresh list
          this.displayToast('File attached successfully');
        },
        error: (err) => {
          console.error('Failed to link file', err);
          this.isAttaching = false;
          const errorMessage = err.error?.detail || 'Failed to attach file';
          this.displayToast(errorMessage, 'error');
        },
      });
  }

  closeAttachModal() {
    this.showAttachModal = false;
  }

  downloadFile(
    file: LeadFile | OutreachAttachment,
    performActualDownload = false,
  ) {
    const fileId =
      (file as LeadFile).file?.id || (file as OutreachAttachment).file_id;
    const fileName =
      (file as LeadFile).file?.file_name ||
      (file as LeadFile).file?.blob_name ||
      (file as OutreachAttachment).file_name;

    if (fileId) {
      this.fileUploadService
        .downloadFile(fileId, !performActualDownload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (performActualDownload) {
              const link = document.createElement('a');
              link.href = res.download_url;
              link.download = res.file_name || fileName;
              link.click();
            } else {
              this.viewerFileId = fileId;
              this.viewerFileUrl = res.download_url;
              this.viewerFileName = res.file_name || fileName;
              this.showFileViewer = true;
            }
          },
          error: (err) => console.error('Download failed', err),
        });
    }
  }

  onCloseViewer(): void {
    this.showFileViewer = false;
    this.viewerFileUrl = '';
    this.viewerFileName = '';
    this.viewerFileId = null;
  }

  onViewerDownload(): void {
    if (this.viewerFileId) {
      this.fileUploadService
        .downloadFile(this.viewerFileId, false)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const link = document.createElement('a');
            link.href = res.download_url;
            link.download = res.file_name;
            link.click();
          },
          error: (err) => console.error('Download failed', err),
        });
    }
  }

  canManage(leadFile: LeadFile): boolean {
    const currentUser = this.authService.getLoggedInUserInfo();
    if (!currentUser) return false;
    // Check if the current user is the uploader
    return leadFile.file.uploaded_user?.username === currentUser.username;
  }

  confirmDelete(leadFile: LeadFile) {
    this.fileToDelete = leadFile;
    this.showDeleteModal = true;
  }

  proceedWithDelete() {
    if (!this.fileToDelete || !this.recordUniqueId) return;

    this.isDeleting = true;
    this.leadFileService
      .removeFileFromLead(this.recordUniqueId, this.fileToDelete.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isDeleting = false;
          this.closeDeleteModal();
          this.loadFiles();
          this.displayToast('Attachment unlinked successfully');
        },
        error: (err) => {
          console.error('Delete failed', err);
          this.isDeleting = false;
          this.displayToast('Failed to unlink attachment', 'error');
        },
      });
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.fileToDelete = null;
  }

  openShareModal(leadFile: LeadFile) {
    if (!this.recordUniqueId) return;

    this.editingFileId = leadFile.id;
    this.showShareModal = true;
    this.isLoadingSharing = true;
    this.selectedConnectionIds.clear();
    this.initialSharedIds.clear();

    this.leadFileService
      .getShareDetails(this.recordUniqueId, leadFile.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          res.connections.forEach((c) => {
            this.selectedConnectionIds.add(c.id);
            this.initialSharedIds.add(c.id);
          });
          this.isLoadingSharing = false;
        },
        error: (err) => {
          console.error('Failed to get share details', err);
          this.isLoadingSharing = false;
        },
      });
  }

  saveSharing() {
    if (!this.editingFileId || !this.recordUniqueId) return;
    this.isSavingSharing = true;

    const currentIds = Array.from(this.selectedConnectionIds);
    const initialIds = Array.from(this.initialSharedIds);

    const toShare = currentIds.filter((id) => !initialIds.includes(id));
    const toUnshare = initialIds.filter((id) => !currentIds.includes(id));

    if (toShare.length === 0 && toUnshare.length === 0) {
      this.closeShareModal();
      this.isSavingSharing = false;
      return;
    }

    this.leadFileService
      .updateSharing(this.recordUniqueId, this.editingFileId, {
        share_connection_ids: toShare,
        unshare_connection_ids: toUnshare,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSavingSharing = false;
          this.closeShareModal();
          this.loadFiles();
          this.displayToast('Sharing settings updated successfully');
        },
        error: (err) => {
          console.error('Update sharing failed', err);
          this.isSavingSharing = false;
          this.displayToast('Failed to update sharing settings', 'error');
        },
      });
  }

  closeShareModal() {
    this.showShareModal = false;
    this.editingFileId = null;
  }

  getFileName(file: LeadFile): string {
    return file.file.file_name || file.file.blob_name || 'Unknown File';
  }

  get isAllSelected(): boolean {
    if (this.sortedConnections.length === 0) return false;
    return this.sortedConnections.every((c) =>
      this.selectedConnectionIds.has(c.id),
    );
  }

  get isAttachButtonDisabled(): boolean {
    return (
      this.isAttaching ||
      (this.isUploading &&
        (!this.selectedUploadFile || !this.hasConsentChecked)) ||
      (!this.isUploading && this.selectedFileIds.size === 0)
    );
  }

  get attachButtonText(): string {
    return this.isAttaching ? 'Attaching...' : 'Attach to Record';
  }

  toggleAllConnections() {
    if (this.isAllSelected) {
      this.selectedConnectionIds.clear();
    } else {
      this.sortedConnections.forEach((c) =>
        this.selectedConnectionIds.add(c.id),
      );
    }
  }
}
